import React from 'react';
import { motion } from 'framer-motion';
import {
  Code,
  Database,
  Monitor,
  Server,
  Palette,
  Globe,
  GitBranch,
  Zap
} from 'lucide-react';

const Skills = ({ skills }) => {
  // Core tech stack with icons
  const techStack = [
    { name: 'React.js', icon: Code, category: 'Frontend', color: 'text-blue-400' },
    { name: 'Tailwind CSS', icon: Palette, category: 'Frontend', color: 'text-cyan-400' },
    { name: 'JavaScript', icon: Zap, category: 'Frontend', color: 'text-yellow-400' },
    { name: 'HTML5', icon: Monitor, category: 'Frontend', color: 'text-orange-400' },
    { name: 'CSS3', icon: Palette, category: 'Frontend', color: 'text-blue-500' },
    { name: 'TypeScript', icon: Code, category: 'Frontend', color: 'text-sky-400' },
    { name: 'Node.js', icon: Server, category: 'Backend', color: 'text-green-400' },
    { name: 'Express.js', icon: Server, category: 'Backend', color: 'text-gray-400' },
    { name: 'MongoDB', icon: Database, category: 'Backend', color: 'text-emerald-400' },
    { name: 'Firebase', icon: Database, category: 'Backend', color: 'text-yellow-500' },
    { name: 'REST API', icon: Globe, category: 'Backend', color: 'text-purple-400' },
    { name: 'Git', icon: GitBranch, category: 'Tools', color: 'text-orange-500' },
    { name: 'GitHub', icon: GitBranch, category: 'Tools', color: 'text-gray-300' },
    { name: 'VS Code', icon: Code, category: 'Tools', color: 'text-sky-500' },
    { name: 'Figma', icon: Palette, category: 'Design', color: 'text-purple-500' }
  ];

  const currentlyLearning = ['React Native', 'GraphQL', 'Docker', 'AWS'];

  return (
    <section id="skills" className="relative py-20 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2 className="section-title gradient-text mb-6">
            Tech Stack
          </motion.h2>
          <motion.p className="section-description">
            Technologies I use to bring ideas to life
          </motion.p>
        </motion.div>

        {/* Tech Stack Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16">
          {techStack.map((tech, index) => {
            const IconComponent = tech.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass card p-6 rounded-xl border border-purple-500/20 hover:border-purple-400/50 transition-all duration-300 text-center group cursor-pointer"
              >
                <motion.div
                  whileHover={{ rotate: 5 }}
                  className="flex justify-center mb-3"
                >
                  <IconComponent className={`w-8 h-8 ${tech.color} group-hover:scale-110 transition-transform duration-300`} />
                </motion.div>
                <h3 className="text-white font-medium text-sm mb-1">{tech.name}</h3>
                <span className="text-gray-400 text-xs">{tech.category}</span>
              </motion.div>
            );
          })}
        </div>

        {/* Currently Learning */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-xl font-bold text-white mb-6">Currently Learning</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {currentlyLearning.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="glass px-4 py-2 rounded-full border border-purple-500/30 hover:border-purple-400/60 transition-all duration-300"
              >
                <span className="text-purple-300 text-sm font-medium">{tech}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-4">
            Always expanding my toolkit to build better solutions
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;